import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { C } from "../ipd-billing/constants";
import {
  assignDiagnosticDoctors,
  getEligibleDiagnosticDoctors,
  LAB_SERVICE_GROUP_ID,
  ASSIGNMENT_STATUS_META,
  PUBLISH_STATUS_META,
  publishRuleHint,
} from "./diagnosticReportApi";

function StatusChip({ meta }) {
  if (!meta) return null;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        color: meta.color,
        background: meta.bg,
      }}
    >
      {meta.label}
    </span>
  );
}

/**
 * Lab / radiology user: pick diagnostic doctors for one order line
 * (patient_lab_test_id from orders[]).
 */
export default function AssignDiagnosticDoctorsModal({
  open,
  onClose,
  patientLabTestId,
  classificationName = "",
  serviceGroupId = LAB_SERVICE_GROUP_ID,
  orderLabel = "",
  departmentLabel = "",
  roleType = "pathologist",
  onAssigned,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [publishRule, setPublishRule] = useState("");
  const [serviceName, setServiceName] = useState("");
  const [resolvedClassification, setResolvedClassification] = useState("");
  const [canPublish, setCanPublish] = useState(false);
  const [listOnlyMode, setListOnlyMode] = useState(false);

  const signedIds = useMemo(
    () =>
      new Set(
        assignments
          .filter((a) => a.status === "signed")
          .map((a) => Number(a.diagnostic_doctor_id))
      ),
    [assignments]
  );

  const load = useCallback(async () => {
    const pltId = Number(patientLabTestId);
    const hasPltId = Number.isFinite(pltId) && pltId > 0;
    const className = classificationName || departmentLabel;

    if (!hasPltId && !className) {
      toast.error("patient_lab_test_id missing on this test line");
      return;
    }

    setLoading(true);
    setListOnlyMode(false);
    try {
      let res;
      if (hasPltId) {
        res = await getEligibleDiagnosticDoctors({
          patientLabTestId: pltId,
          roleType,
        });
      } else {
        res = await getEligibleDiagnosticDoctors({
          classificationName: className,
          serviceGroupId,
          roleType,
        });
        setListOnlyMode(true);
      }

      const eligible = Array.isArray(res.doctors) ? res.doctors : [];
      const current = Array.isArray(res.assignments) ? res.assignments : [];
      setDoctors(eligible);
      setAssignments(current);
      setPublishRule(res.publish_rule || "");
      setServiceName(res.service_name || "");
      setResolvedClassification(res.classification_name || className || "");
      setCanPublish(res.can_publish === true);
      setSelectedIds(
        current.map((a) => Number(a.diagnostic_doctor_id)).filter(Number.isFinite)
      );
    } catch (err) {
      console.error("getEligibleDiagnosticDoctors:", err);
      toast.error(err?.message || "Could not load eligible doctors");
    } finally {
      setLoading(false);
    }
  }, [
    classificationName,
    departmentLabel,
    patientLabTestId,
    roleType,
    serviceGroupId,
  ]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const toggleDoctor = (doctorPk) => {
    if (signedIds.has(doctorPk)) return;
    setSelectedIds((prev) =>
      prev.includes(doctorPk)
        ? prev.filter((id) => id !== doctorPk)
        : [...prev, doctorPk]
    );
  };

  const handleSave = async () => {
    const pltId = Number(patientLabTestId);
    if (!Number.isFinite(pltId) || pltId <= 0) {
      toast.error(
        "patient_lab_test_id is required to assign doctors. Expand the patient row to refresh order details.",
      );
      return;
    }
    if (selectedIds.length === 0) {
      toast.error("Select at least one doctor to send the report");
      return;
    }

    // Replace mode: send full current list; signed doctors stay included + locked.
    const diagnosticDoctorIds = [
      ...new Set([
        ...selectedIds.map(Number),
        ...Array.from(signedIds),
      ]),
    ].filter(Number.isFinite);

    setSaving(true);
    try {
      const res = await assignDiagnosticDoctors(pltId, diagnosticDoctorIds);
      toast.success(res.message || "Report sent for review");
      onAssigned?.(res.data);
      onClose?.();
    } catch (err) {
      console.error("assignDiagnosticDoctors:", err);
      toast.error(err?.message || "Could not assign doctors");
    } finally {
      setSaving(false);
    }
  };

  const displayClassification =
    resolvedClassification || departmentLabel || classificationName;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 2200 }}
      BackdropProps={{ sx: { zIndex: 2199 } }}
      PaperProps={{ sx: { zIndex: 2200 } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        Submit report for review
      </DialogTitle>
      <DialogContent dividers>
        {orderLabel && (
          <Typography variant="body2" sx={{ mb: 0.5, color: C.textSub }}>
            {orderLabel}
          </Typography>
        )}
        {serviceName && (
          <Typography variant="body1" sx={{ mb: 1, fontWeight: 700 }}>
            {serviceName}
          </Typography>
        )}
        {displayClassification && (
          <Typography variant="body2" sx={{ mb: 1.5, color: C.textSub }}>
            Classification:{" "}
            <strong style={{ color: C.text }}>{displayClassification}</strong>
          </Typography>
        )}
        {patientLabTestId && (
          <Typography variant="caption" sx={{ color: C.textSub, display: "block", mb: 1 }}>
            Order ref: patient_lab_test_id {patientLabTestId}
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
          <StatusChip meta={PUBLISH_STATUS_META.pending} />
          {canPublish && (
            <Typography variant="caption" sx={{ color: C.success }}>
              Ready to publish after sign-off
            </Typography>
          )}
          {publishRule && (
            <Typography variant="caption" sx={{ color: C.textSub }}>
              {publishRuleHint(publishRule)}
            </Typography>
          )}
        </Box>

        {listOnlyMode && (
          <Typography variant="caption" sx={{ color: C.warning, display: "block", mb: 1 }}>
            Showing doctors by classification only — assign needs patient_lab_test_id on this test line.
          </Typography>
        )}

        {assignments.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Already assigned
            </Typography>
            {assignments.map((a) => (
              <Box
                key={a.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 0.5,
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                <Typography variant="body2">{a.name}</Typography>
                <StatusChip meta={ASSIGNMENT_STATUS_META[a.status]} />
              </Box>
            ))}
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : doctors.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No {roleType === "radiologist" ? "radiologists" : "pathologists"} configured for {displayClassification || "this classification"}.
            Add diagnostic doctors in admin with matching service group/classification.
          </Typography>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Select doctor(s) for review ({doctors.length})
            </Typography>
            {doctors.map((doc) => {
              const pk = Number(doc.id);
              const isSigned = signedIds.has(pk);
              const checked = selectedIds.includes(pk) || isSigned;
              return (
                <FormControlLabel
                  key={pk}
                  sx={{ display: "flex", alignItems: "flex-start", mb: 0.5 }}
                  control={
                    <Checkbox
                      checked={checked}
                      disabled={isSigned}
                      onChange={() => toggleDoctor(pk)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {doc.name || doc.username}
                        {isSigned ? " (signed — locked)" : ""}
                      </Typography>
                      <Typography variant="caption" sx={{ color: C.textSub }}>
                        {[doc.specialization, doc.qualifications, doc.registration_no]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </Box>
                  }
                />
              );
            })}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            saving ||
            loading ||
            doctors.length === 0 ||
            listOnlyMode ||
            !patientLabTestId
          }
          sx={{ backgroundColor: C.primary }}
        >
          {saving ? "Submitting…" : "Submit for Review"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

